import type React from 'react';

import { AdornedInput } from '@zillow/constellation-10';

type Props = {
  value: string | number;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  disabled?: boolean;
  error?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'data-test'?: string;
  'data-field-name'?: string;
  'data-index'?: number;
  name?: string;
  endAdornment?: React.ReactNode;
};

/**
 * Formats a number with commas and 2 decimal places
 * e.g., 1234.5 -> "1,234.50"
 */
function formatCurrency(value: string): string {
  if (!value || value.trim() === '') {
    return '';
  }

  const numericValue = parseFloat(value.replace(/[^0-9.]/g, ''));

  if (isNaN(numericValue)) {
    return '';
  }

  return numericValue.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function CurrencyInput({
  value,
  onChange,
  onBlur,
  onFocus,
  disabled,
  error,
  endAdornment,
  ...rest
}: Props): JSX.Element {
  function handleBlur(event: React.FocusEvent<HTMLInputElement>): void {
    const rawValue = event.currentTarget.value;
    const formattedValue = formatCurrency(rawValue);

    // Create proxies to preserve dataset and other getters while overriding value
    const currentTargetProxy = new Proxy(event.currentTarget, {
      get(target, prop) {
        if (prop === 'value') {
          return formattedValue;
        }
        return target[prop as keyof HTMLInputElement];
      },
    });

    const targetProxy = new Proxy(event.target as HTMLInputElement, {
      get(target, prop) {
        if (prop === 'value') {
          return formattedValue;
        }
        return target[prop as keyof HTMLInputElement];
      },
    });

    // Call the original onBlur if provided, otherwise call onChange with formatted value
    if (onBlur) {
      const syntheticEvent = {
        ...event,
        currentTarget: currentTargetProxy,
        target: targetProxy,
      } as unknown as React.FocusEvent<HTMLInputElement>;
      onBlur(syntheticEvent);
    } else {
      const changeEvent = {
        ...event,
        currentTarget: currentTargetProxy,
        target: targetProxy,
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      onChange(changeEvent);
    }
  }

  return (
    <AdornedInput.Root error={error} disabled={disabled}>
      <AdornedInput.Adornment>$</AdornedInput.Adornment>
      <AdornedInput.Input
        value={value}
        onChange={onChange}
        onBlur={handleBlur}
        onFocus={onFocus}
        type="text"
        inputMode="decimal"
        disabled={disabled}
        {...rest}
      />
      {endAdornment && <AdornedInput.Adornment>{endAdornment}</AdornedInput.Adornment>}
    </AdornedInput.Root>
  );
}

// Export the format function for use elsewhere
export { formatCurrency };
